import { useMemo, useState } from 'react';
import { toast } from '@/lib/toast';
import type { JSX } from 'react';
import { FormProvider, type FieldValues } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Site } from '@/data/schemas';
import { useDataProvider } from '@/data/provider';
import { queryKeys } from '@/lib/query-keys';
import { useUnifiedForm } from '../../../../hooks/useUnifiedForm';
import {
  siteFieldOrder,
  siteFormFields,
  siteFormSchema,
  siteFormSections,
  type SiteFormValues,
} from '../../components/siteFormSchema';
import {
  EntityDialog,
  EntityDialogFooter,
  EntityFormFields,
  FormErrorBanner,
  FormErrorSummary,
  useEntityDialogForm,
  useEntityDialogSubmit,
} from '@/components/blocks/forms';
import { Button, DialogClose } from '@/components/ui';
import { scrollToFirstError } from '@/components/blocks/forms/formErrors';
import { ConfirmDialog } from '@/components/blocks/feedback/ConfirmDialog';

interface SiteModalProps {
  site: Site | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contextLine?: string;
  onUpdated?: (site: Site) => void;
  onDelete?: () => void;
  isDeleting?: boolean;
}

export function SiteModal({
  site,
  open,
  onOpenChange,
  contextLine,
  onUpdated,
  onDelete,
  isDeleting = false,
}: SiteModalProps): JSX.Element {
  const queryClient = useQueryClient();
  const { sites } = useDataProvider();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { fields } = useUnifiedForm({
    schema: siteFormSchema,
    fields: siteFormFields,
    fieldOrder: siteFieldOrder,
    fieldOverrides: {
      notes: { colSpan: 2 },
    },
  });

  const updateSiteMutation = useMutation({
    mutationFn: async (values: SiteFormValues) => {
      if (!site) throw new Error('Site not available');

      await sites.updateSite(site.id, {
        name: values.name,
        sedeOperativaAddress: values.sedeOperativaAddress,
        sedeOperativaCap: values.sedeOperativaCap,
        sedeOperativaCity: values.sedeOperativaCity,
        sedeOperativaProvince: values.sedeOperativaProvince,
        sedeOperativa: undefined,
        periodicity: values.periodicity as '1' | '3' | '4' | '6',
        contractActivationDate: values.contractActivationDate,
        notes: values.notes,
        // CPI fields
        cpiPresent: values.cpiPresent,
        cpiProtocolNumber: values.cpiProtocolNumber,
        cpiLastRenewalDate: values.cpiLastRenewalDate,
        cpiExpiryDate: values.cpiExpiryDate,
        ammoniaActivityCodes: values.ammoniaActivityCodes,
        // Authorization fields
        authorizationProtocolNumber: values.authorizationProtocolNumber,
        authorizationIssueDate: values.authorizationIssueDate,
        authorizationExpiryDate: values.authorizationExpiryDate,
        authorizedNh3Kg: values.authorizedNh3Kg,
        officialGazettePublished: values.officialGazettePublished,
        officialGazetteDate: values.officialGazetteDate,
      });
    },
    onSuccess: async () => {
      if (!site) return;

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.companies.all }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.companies.detail(site.companyId),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.sites.detail(site.id),
        }),
      ]);

      const refreshed = await sites.getSite(site.id);
      toast.success('Stabilimento aggiornato con successo');
      if (refreshed) {
        onUpdated?.(refreshed);
      }
      onOpenChange(false);
    },
  });

  const formData = useMemo(() => {
    if (!site) return null;

    return {
      name: site.name,
      sedeOperativaAddress: site.sedeOperativaAddress ?? '',
      sedeOperativaCap: site.sedeOperativaCap ?? '',
      sedeOperativaCity: site.sedeOperativaCity ?? '',
      sedeOperativaProvince: site.sedeOperativaProvince ?? '',
      periodicity: site.periodicity ?? '1',
      contractActivationDate: site.contractActivationDate ?? '',
      // CPI fields
      cpiPresent: site.cpiPresent ?? undefined,
      cpiProtocolNumber: site.cpiProtocolNumber ?? '',
      cpiLastRenewalDate: site.cpiLastRenewalDate ?? undefined,
      cpiExpiryDate: site.cpiExpiryDate ?? undefined,
      ammoniaActivityCodes: site.ammoniaActivityCodes ?? undefined,
      // Authorization fields
      authorizationProtocolNumber: site.authorizationProtocolNumber ?? '',
      authorizationIssueDate: site.authorizationIssueDate ?? '',
      authorizationExpiryDate: site.authorizationExpiryDate ?? '',
      authorizedNh3Kg: site.authorizedNh3Kg ?? undefined,
      officialGazettePublished: site.officialGazettePublished ?? undefined,
      officialGazetteDate: site.officialGazetteDate ?? undefined,
      authorizationPrescriptions: site.authorizationPrescriptions ?? '',
      reperibilePhone: site.reperibilePhone ?? '',
      notes: site.notes ?? undefined,
    } satisfies SiteFormValues;
  }, [site]);

  const { form, formId, errorCount, rootError, handleInvalidSubmit, setRootError } =
    useEntityDialogForm({
      open,
      schema: siteFormSchema,
      fields,
      data: formData ?? null,
    });

  const handleSubmit = useEntityDialogSubmit({
    form,
    onSubmit: async (values: FieldValues) => {
      await updateSiteMutation.mutateAsync(values as SiteFormValues);
    },
    setRootError,
  });

  if (!site || !formData) {
    return <></>;
  }

  const handleArchive = async () => {
    if (!onDelete) return;
    try {
      await onDelete();
      setConfirmOpen(false);
    } catch (error) {
      setRootError('Impossibile archiviare. Riprova o contatta l’assistenza.');
      throw error;
    }
  };

  const isSaving = updateSiteMutation.isPending;
  const isDirty = form.formState.isDirty;

  const sections = siteFormSections;

  return (
    <>
      <EntityDialog
        open={open}
        onOpenChange={onOpenChange}
        title={site.name}
        size="md"
        isDirty={isDirty}
        isSaving={isSaving}
        formId={formId}
        contextLine={contextLine}
        banner={rootError ? <FormErrorBanner message={rootError} /> : null}
        summary={
          errorCount > 0 ? (
            <FormErrorSummary
              count={errorCount}
              onJumpToFirstError={() => scrollToFirstError(form.formState.errors)}
            />
          ) : null
        }
        footer={
          <EntityDialogFooter
            extra={
              onDelete ? (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setConfirmOpen(true)}
                  disabled={isSaving || isDeleting}
                >
                  {isDeleting ? 'Archiviazione…' : 'Archivia stabilimento'}
                </Button>
              ) : null
            }
            secondary={
              <DialogClose asChild>
                <Button variant="ghost" disabled={isSaving || isDeleting}>
                  Chiudi
                </Button>
              </DialogClose>
            }
            primary={
              <Button type="submit" form={formId} disabled={!isDirty || isSaving || isDeleting}>
                {isSaving ? 'Salvataggio…' : 'Salva'}
              </Button>
            }
          />
        }
      >
        <FormProvider {...form}>
          <form
            id={formId}
            onSubmit={form.handleSubmit(handleSubmit, handleInvalidSubmit)}
            noValidate
            className="space-y-4"
          >
            <EntityFormFields
              fields={fields}
              sections={sections}
              disabled={isSaving || isDeleting}
            />
          </form>
        </FormProvider>
      </EntityDialog>

      {onDelete ? (
        <ConfirmDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          title="Archiviare stabilimento?"
          message="L’elemento verrà archiviato e non sarà più visibile nelle liste."
          cancelLabel="Annulla"
          confirmLabel="Archivia"
          variant="danger"
          onConfirm={handleArchive}
          isLoading={isDeleting}
        />
      ) : null}
    </>
  );
}
